import { Skeleton, Stack, Typography } from "@mui/material";
import { type FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterRailFrame } from "../../../../components/UIToolkit/Filters/FilterRailFrame";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import type { PbjBuildingAuditSummary } from "../../../../utils/models";
import { BuildingFilterRail } from "../../network/BuildingFilterRail";
import type { BuildingQueryState, BuildingResult } from "../../network/buildingQueries";
import { addBuildingSummaries, cleanShare, hoursToReview, selectedResults } from "../../network/networkMath";
import { usePbjNetwork } from "../../network/PbjNetworkContext";
import type { OpenAuditDestination } from "../auditSubTabs";
import { type BuildingSummaryRow, BuildingsSummaryTable } from "./BuildingsSummaryTable";
import { CmsDeadlineChip } from "./CmsDeadlineChip";
import { ReviewHoursCard } from "./ReviewHoursCard";
import { SummaryTiles } from "./SummaryTiles";
import { type SummaryFormatters, useSummaryFormatters } from "./useSummaryFormatters";
import { WhereToStartCard } from "./WhereToStartCard";

const CARD_SKELETON_HEIGHT_PX = 220;
// DEFAULT: no rows while any building is loading or after one fails. The cards and table then show their
// loading or error state and the tiles are hidden on error, so these never reach the screen as zeros.
const NO_ROWS: BuildingSummaryRow[] = [];

type Props = {
	summaries: BuildingQueryState<BuildingResult<PbjBuildingAuditSummary>[]>;
	committedEnd: string;
	onOpen: OpenAuditDestination;
};

/** The selected buildings' Summary rows, each with its hours to review and clean share. */
const useSummaryRows = (summaries: Props["summaries"]): BuildingSummaryRow[] => {
	const { buildingName, selectedBuildingIds } = usePbjNetwork();
	return useMemo(
		() =>
			summaries.data
				? selectedResults(summaries.data, selectedBuildingIds).map(({ data }) => {
						const review = hoursToReview(data);
						return { ...data, buildingName: buildingName(data.departmentId), hoursToReview: review, clean: cleanShare(data.totalHours, review) };
					})
				: NO_ROWS,
		[summaries.data, selectedBuildingIds, buildingName],
	);
};

/** The Buildings table of a network Summary: one row per building, each count opening its list. */
const BuildingsSection: FC<{ rows: BuildingSummaryRow[]; summaries: Props["summaries"]; formatters: SummaryFormatters; onOpen: OpenAuditDestination }> = ({
	rows,
	summaries,
	formatters,
	onOpen,
}) => {
	const { t } = useTranslation();
	return (
		<>
			<Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
				<Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
					{t("pbj_model.network.summary.buildings.title")}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{t("pbj_model.network.summary.buildings.subtitle")}
				</Typography>
			</Stack>
			<BuildingsSummaryTable rows={rows} loading={summaries.isLoading} error={summaries.isError} formatters={formatters} onOpen={onOpen} />
		</>
	);
};

/**
 * The Audit Summary: everything the other Audit tabs flag, each figure opening its list. For a network it is
 * rolled up by building — with the Building filter, "Where to start" and the Buildings table; for one building
 * those three would only repeat the figures above them, so they're left out.
 */
export const NetworkSummaryTab: FC<Props> = ({ summaries, committedEnd, onOpen }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { isNetwork, homeDepartmentId, buildings } = usePbjNetwork();
	const formatters = useSummaryFormatters();
	const rows = useSummaryRows(summaries);
	const totals = useMemo(() => addBuildingSummaries(rows), [rows]);
	const reviewHours = hoursToReview(totals);
	const clean = cleanShare(totals.totalHours, reviewHours);

	useEffect(() => {
		track("pbj_network_summary_viewed", { department_id: homeDepartmentId, building_count: buildings.length });
	}, [track, homeDepartmentId, buildings.length]);

	return (
		<Stack spacing={1.5} data-testid="pbj-network-summary">
			<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
				<Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
					{t("pbj_model.network.summary.title")}
				</Typography>
				<CmsDeadlineChip committedEnd={committedEnd} />
			</Stack>
			<Typography variant="body2" color="text.secondary">
				{t(isNetwork ? "pbj_model.network.summary.subtitle" : "pbj_model.network.summary.subtitle_one_building")}
			</Typography>
			<FilterRailFrame flat rail={isNetwork && <BuildingFilterRail dataTestId="pbj-network-summary-filters" />} dataTestId="pbj-network-summary-frame">
				<Stack spacing={2} sx={{ p: 2 }}>
					{summaries.isError ? (
						<Typography variant="body2" color="error.main" data-testid="pbj-network-summary-error">
							{t("pbj_model.network.summary.failed")}
						</Typography>
					) : summaries.data === undefined ? (
						<Skeleton variant="rounded" height={CARD_SKELETON_HEIGHT_PX} data-testid="pbj-network-summary-loading" />
					) : (
						<Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
							<ReviewHoursCard totals={totals} reviewHours={reviewHours} clean={clean} formatters={formatters} onOpen={onOpen} />
							{isNetwork && <WhereToStartCard rows={rows} formatHours={formatters.formatHours} onOpen={onOpen} />}
						</Stack>
					)}
					{!summaries.isError && <SummaryTiles totals={totals} clean={clean} loading={summaries.data === undefined} formatters={formatters} />}
					{isNetwork && <BuildingsSection rows={rows} summaries={summaries} formatters={formatters} onOpen={onOpen} />}
				</Stack>
			</FilterRailFrame>
		</Stack>
	);
};
